import { Shield, Thermometer, Volume2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const AuthorityContentSection = () => {
  return (
    <section className="py-20 bg-background" aria-label="Why choose our secondary glazing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-primary font-montserrat mb-4">
          The Ultimate Solution for Soundproofing &amp; Thermal Insulation
        </h2>
        <div className="w-24 h-1 bg-accent mx-auto mb-12" aria-hidden="true" />

        {/* 3-column highlights */}
        <div className="grid md:grid-cols-3 gap-10 mb-16">
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Volume2 className="h-7 w-7 text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-3 font-montserrat">
              Maximum Noise Reduction
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Our 10.8&nbsp;mm acoustic laminate glass achieves up to 80% noise reduction (45&nbsp;dB), transforming even the busiest London streets into a peaceful retreat. Ideal for homes near main roads, railway lines, and flight paths.
            </p>
            <Link to="/acoustic-glass-technical-guide" className="text-sm font-semibold text-accent hover:text-accent/80 transition-colors inline-flex items-center gap-1">
              Read the Technical Guide →
            </Link>
          </div>

          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Thermometer className="h-7 w-7 text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-3 font-montserrat">
              Superior Thermal Efficiency
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              Cut heat loss by up to 65% and noticeably reduce your energy bills from the first winter. The insulating air gap between your original window and the secondary panel traps warmth where it belongs—inside your home.
            </p>
          </div>

          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="h-7 w-7 text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-3 font-montserrat">
              Listed Building Approved
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Fully compliant with Conservation Area regulations and English Heritage guidance. Our slim-profile aluminium frames sit discreetly behind original sash windows, so you improve performance without altering the building's character.
            </p>
            <Link to="/listed-building-secondary-glazing-guide" className="text-sm font-semibold text-accent hover:text-accent/80 transition-colors inline-flex items-center gap-1">
              Read the Heritage Masterclass →
            </Link>
          </div>
        </div>

        {/* Expert body text */}
        <div className="max-w-4xl mx-auto">
          <p className="text-muted-foreground leading-relaxed mb-4">
            Our secondary glazing London service covers homes and commercial properties across the capital, from central conservation areas to busy road, rail and flight-path locations in outer London. Explore our{" "}
            <Link to="/london-boroughs-secondary-glazing" className="font-semibold text-accent hover:text-accent/80 transition-colors">
              London borough secondary glazing guide
            </Link>{" "}
            to find local advice, likely noise sources and property-specific specifications for your area.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            For over a decade we have been fitting bespoke secondary glazing to period homes across London—from draughty Victorian sashes in Kensington to grand Georgian villas in Belgravia. Every unit is precision-measured on site and manufactured to the exact profile of your existing window, ensuring an airtight seal without the need for planning permission or structural alteration.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Unlike replacement double glazing, secondary glazing preserves your original timber or metal frames while delivering comparable—or superior—acoustic and thermal performance. Our range includes horizontal sliders, vertical sliders, hinged casements, and fixed panels, each engineered from extruded aluminium and available in over 200 RAL colours to complement any interior.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-6">
            Whether you are a homeowner tackling rising energy costs, a landlord meeting EPC requirements, or a conservation officer safeguarding heritage assets, our team will design a solution tailored to your property. We handle everything from the initial survey through to final commissioning, backed by a comprehensive 12-year warranty.
          </p>
          <div className="text-center">
            <Link to="/get-quote">
              <Button className="bg-accent hover:bg-accent-light text-primary font-semibold px-8 py-3 text-lg">
                Request a London Secondary Glazing Quote
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
